import { isEqual } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { LongText } from '../../shared/longtext/LongText';
import { Text } from '../../shared/text/Text';
import { getSelectedRole, updateRoleValue, Role, upsertRoleStarted, getAllRoles, getSavedSelectedRole, getRoleModalOpen, toggleRoleModal } from './store';
import styles from './Roles.module.scss';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { FeaturePermissions } from '../users/FeaturePermissions';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { getAllAccessGroups } from '../access-groups/store';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { getAllUsers } from '../users/store';

export const RoleDialog: React.FC = () => {
    const dispatch = useAppDispatch();

    const selectedRole = useAppSelector(getSelectedRole);
    const savedSelectedRole = useAppSelector(getSavedSelectedRole);
    const allRoles = useAppSelector(getAllRoles);
    const roleModalOpen = useAppSelector(getRoleModalOpen);
    const allAccessGroups = useAppSelector(getAllAccessGroups);
    const allUsers = useAppSelector(getAllUsers);

    const updateRole = useCallback((key: keyof Role, value: string | number[] | null) => dispatch(updateRoleValue(key, value)), [dispatch]);
    const closeModal = useCallback(() => dispatch(toggleRoleModal(false)), [dispatch]);
    const saveRole = useCallback(() => dispatch(upsertRoleStarted()), [dispatch]);

    const { roleName, roleDescription, featurePermissions, accessGroupIds, userIds } = selectedRole;

    const roleNameExists = useMemo(() => {
        let existingRoles = allRoles;
        if (selectedRole.roleId) {
            existingRoles = allRoles.filter(({ roleId }) => roleId !== selectedRole.roleId);
        }
        return existingRoles.some(({ roleName }) => roleName.toLowerCase() === selectedRole.roleName.toLowerCase());
    }, [selectedRole, allRoles]);

    const selectedRoleUpdated = useMemo(() => !isEqual(selectedRole, savedSelectedRole), [selectedRole, savedSelectedRole]);
    const selectedRoleNameIsEmpty = useMemo(() => selectedRole.roleName === '', [selectedRole.roleName]);

    const saveDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (selectedRoleNameIsEmpty && selectedRoleUpdated) {
            disabledTooltip.push('A role can not be saved without a name.');
        }
        if (roleNameExists) {
            disabledTooltip.push('The name you have chosen cannot be used as it already exists as a role.');
        }
        if (selectedRole.featurePermissions.length === 0) {
            disabledTooltip.push('You must choose at least one feature permission.');
        }
        return disabledTooltip;
    }, [selectedRole, selectedRoleUpdated, roleNameExists, selectedRoleNameIsEmpty]);

    const updateFeaturePermissions = useCallback((permissions: number[]) => dispatch(updateRole('featurePermissions', permissions)), [dispatch, updateRole]);

    const accessGroupOptions = useMemo(() => allAccessGroups.map(({ accessGroupName, accessGroupId }) => ({ value: accessGroupId!.toString(), label: accessGroupName })), [allAccessGroups]);

    const selectedAccessGroups = useMemo(() => {
        if (accessGroupIds.length > 0) {
            return allAccessGroups.filter(({ accessGroupId }) => accessGroupIds.includes(accessGroupId!)).map(({ accessGroupId, accessGroupName }) => ({ label: accessGroupName, value: accessGroupId!.toString() }));
        }
        return null;
    }, [allAccessGroups, accessGroupIds]);

    const updateDropdown = useCallback((option: DropdownOption | Options<DropdownOption> | null, key: keyof Role) => {
        let selected: number[] = [];
        if (!isNull(option) && (option as Options<DropdownOption>).length > 0) {
            selected = (option as Options<DropdownOption>).map(({ value }) => parseInt(value));
        }
        updateRole(key, selected);
    }, [updateRole]);

    const saveDisabled = useMemo(() => selectedRoleNameIsEmpty || roleNameExists || !selectedRoleUpdated || selectedRole.featurePermissions.length === 0, [selectedRole, selectedRoleUpdated, roleNameExists, selectedRoleNameIsEmpty]);

    const usernameOptions = useMemo(() => allUsers.map(({ userId, username }) => ({ value: userId.toString(), label: username })), [allUsers]);

    const selectedUserIds = useMemo(() => {
        if (userIds.length > 0) {
            return allUsers.filter(({ userId }) => userIds.includes(userId!)).map(({ userId, username }) => ({ label: username, value: userId.toString() }));
        }
        return null;
    }, [allUsers, userIds]);

    return (
        <ConfirmationModal
            isOpen={roleModalOpen}
            closeModal={closeModal}
            confirmLabel='Save'
            confirmDisabled={saveDisabled}
            testId='upsert-role'
            confirm={saveRole}
            confirmDisabledTooltip={saveDisabledTooltip}
        >
            <div className={styles.selectedRoleWrapper}>
                <div className={styles.selectedRole}>
                    <Scrollable>
                        <div className={styles.selectedRoleInput}>
                            <Text
                                testId='role-name'
                                value={roleName}
                                onChange={e => updateRole('roleName', e.target.value)}
                                maxLength={200}
                                label='Name'
                            />
                        </div>
                        <div className={styles.selectedRoleInput}>
                            <LongText
                                testId='role-description'
                                value={roleDescription}
                                onChange={value => updateRole('roleDescription', value)}
                                maxLength={1000}
                                width='100%'
                                label='Description'
                                height='150px'
                            />
                        </div>
                        <div className={styles.selectedRoleInput}>
                            <Dropdown
                                value={selectedAccessGroups}
                                options={accessGroupOptions}
                                testId='access-groups'
                                onChange={e => updateDropdown(e, 'accessGroupIds')}
                                label='Access Groups'
                                marginBottom='10px'
                                menuPortalTarget={document.body}
                                isMulti
                            />
                        </div>
                        <div className={styles.selectedRoleInput}>
                            <Dropdown
                                value={selectedUserIds}
                                options={usernameOptions}
                                testId='user-ids'
                                onChange={e => updateDropdown(e, 'userIds')}
                                label='Users'
                                marginBottom='10px'
                                menuPortalTarget={document.body}
                                isMulti
                            />
                        </div>
                        <FeaturePermissions featurePermissionIds={featurePermissions} roleId={selectedRole.roleId} isAdmin={false} isSystemAdmin={false} updateFeaturePermissions={updateFeaturePermissions} inRoleView />
                    </Scrollable>
                </div>
            </div>
        </ConfirmationModal>
    );
};
